/**
 * pending-confirm.ts (T-09, DS-1g-03 §3.1) — AC-1g-11 (client leg).
 *
 * 1d's W-02 deferred-turn mechanism: Turn A commits a COMPLETE staged
 * record and its reply carries an `await_confirm` field
 * (`{confirm_token, deadline, candidate_summary}`); Turn B is a NEW
 * `/mark/message` Event whose `payload.confirm_resolution` re-enters the
 * skill. The client needs no long-poll/websocket/push channel — it is a
 * plain durable outbox write on receipt, and a plain durable outbox write
 * on the tap.
 *
 * `pending_confirm` is explicitly NOT a fifth `OutboxState` (db.ts's own
 * module doc: "a later task's (T-09) sub-state layered on top of
 * `delivered` — not modeled here") — that would require editing
 * outbox/types.ts, a pre-existing file. Instead it is layered onto the
 * DELIVERED record's own open `payload` bag (the §4.7-established locus
 * for non-schema-literal fields), via `applyAwaitConfirm` below — the ONE
 * place that stamps it.
 *
 * **Receiving the ASK** — `applyAwaitConfirm`: called (by the, deferred,
 * dispatcher/reply-handling wiring — T-28's server leg lands the actual
 * emission) once a DELIVERED capture's reply envelope carries
 * `await_confirm`; durably stamps the field onto that record.
 *
 * **Sending the Verdict** — `submitConfirmVerdict`: the tap creates a NEW
 * capture group (fresh `capture_id`+`client_id`, own outbox row) carrying
 * `payload.confirm_resolution {capture_id, confirm_token, verdict}` —
 * riding the SAME §1 outbox/dispatcher machinery as any other capture
 * (durable, retryable, idempotent server-side per 1d). The Correct verdict
 * additionally carries a human-typed `correction_note` (§5's correction
 * affordance, kept local/inline for Stage A — see PendingConfirmCard).
 * `Confirmation`/`Correction` LABELED EMISSION is server-side (T-28); this
 * module only ever produces the client-side capture group that carries
 * the verdict, never the label itself (AC-1g-11's live proof is GATED
 * per S8).
 *
 * **Timeout/expiry** — `pendingConfirmStatus`: a pure, clock-driven
 * derivation (`now` injectable for tests) — past the Turn-A `deadline`,
 * the status is `"expired"`; the card replaces its tap affordances with a
 * re-open-from-daily-log affordance instead of retrying anything blindly
 * (design §3.1: "a late Turn B... never retries it blindly").
 */

import { getOutboxRecord, putOutboxRecord } from "../outbox/db";
import { inheritedProjectRef, PROJECT_REF_KEY, releaseHeld, saveToProject } from "../outbox/state-machine";
import type { OutboxRecord } from "../outbox/types";

export const CONFIRM_PENDING_KEY = "confirm_pending";
export const CONFIRM_DISPOSITION_KEY = "confirm_disposition";

export interface AwaitConfirmField {
  confirm_token: string;
  deadline: string; // ISO — Turn-A's stated deadline
  candidate_summary: string;
  /**
   * OPAQUE. The capture the goalkeeper staged this confirm against, which is
   * not this record's capture_id: Skill #2 runs on a coordinator fan-out leg
   * with a derived id, because `ops.capture` is keyed by capture_id and the leg
   * cannot reuse the primary row's.
   *
   * This device never interprets it — it echoes it back on Turn B so the
   * coordinator can address the right row. Optional because a Turn A produced
   * before this field existed still has to render rather than crash the card.
   */
  resolves_capture_id?: string;
}

export type ConfirmVerdict = "confirm" | "correct" | "delete";

export type PendingConfirmStatus = "none" | "pending" | "expired";

function awaitConfirmFieldOf(record: Pick<OutboxRecord, "envelope">): AwaitConfirmField | undefined {
  const v = record.envelope.payload[CONFIRM_PENDING_KEY];
  return v && typeof v === "object" ? (v as AwaitConfirmField) : undefined;
}

/**
 * Receiving the ASK: stamps `await_confirm`'s fields onto a DELIVERED
 * record's payload — the ONE locus that flips a record into the
 * `pending_confirm` sub-state. Requires the record already be DELIVERED
 * (design §3.1: this only happens "on a delivered reply").
 */
export async function applyAwaitConfirm(capture_id: string, field: AwaitConfirmField): Promise<OutboxRecord> {
  const record = await getOutboxRecord(capture_id);
  if (!record) throw new Error(`pending-confirm: no outbox record for capture_id "${capture_id}"`);
  if (record.state !== "delivered") {
    throw new Error(
      `pending-confirm: applyAwaitConfirm requires a DELIVERED record (got "${record.state}") for capture_id "${capture_id}"`,
    );
  }
  const updated: OutboxRecord = {
    ...record,
    envelope: { ...record.envelope, payload: { ...record.envelope.payload, [CONFIRM_PENDING_KEY]: field } },
  };
  await putOutboxRecord(updated);
  return updated;
}

/** Clears the pending-confirm marker — used once the verdict's own
 * capture group reaches DELIVERED ("the card resolves on DELIVERED",
 * §3.1) or when a card is re-opened from an expired state. The ONLY
 * other function in this module that writes to the original record. */
export async function clearPendingConfirm(capture_id: string): Promise<OutboxRecord> {
  const record = await getOutboxRecord(capture_id);
  if (!record) throw new Error(`pending-confirm: no outbox record for capture_id "${capture_id}"`);
  const { [CONFIRM_PENDING_KEY]: _drop, ...rest } = record.envelope.payload;
  const updated: OutboxRecord = { ...record, envelope: { ...record.envelope, payload: rest } };
  await putOutboxRecord(updated);
  return updated;
}

/** Pure, clock-driven derivation (AC-1g-11 timeout/expiry UX) — never
 * itself performs a write; `now` is injectable for deterministic tests. */
export function pendingConfirmStatus(
  record: Pick<OutboxRecord, "envelope">,
  now: Date = new Date(),
): PendingConfirmStatus {
  const field = awaitConfirmFieldOf(record);
  if (!field) return "none";
  const disposition = record.envelope.payload[CONFIRM_DISPOSITION_KEY] as
    | { confirm_token?: unknown }
    | undefined;
  if (disposition?.confirm_token === field.confirm_token) return "none";
  return new Date(field.deadline).getTime() <= now.getTime() ? "expired" : "pending";
}

export function awaitConfirmFieldFor(record: Pick<OutboxRecord, "envelope">): AwaitConfirmField | undefined {
  return awaitConfirmFieldOf(record);
}

/** Injectable persistence boundary used by failure-matrix tests. The ordering
 * is intentional: save the stable verdict, release it, then hide the ASK. */
export interface ConfirmVerdictPersistence {
  getRecord: typeof getOutboxRecord;
  saveVerdict: typeof saveToProject;
  releaseVerdict: typeof releaseHeld;
  writeOriginal: typeof putOutboxRecord;
}

const DEFAULT_PERSISTENCE: ConfirmVerdictPersistence = {
  getRecord: getOutboxRecord,
  saveVerdict: saveToProject,
  releaseVerdict: releaseHeld,
  writeOriginal: putOutboxRecord,
};

/**
 * Sending the Verdict: mints a fresh capture group carrying
 * `payload.confirm_resolution`, referencing the original via
 * `capture_id`/`confirm_token`. Throws if the original has no pending
 * `await_confirm` field — structurally, a verdict cannot be submitted for
 * a record that was never asked (there is no other constructor for a
 * `confirm_resolution` payload anywhere in this codebase).
 */
export async function submitConfirmVerdict(
  original: OutboxRecord,
  verdict: ConfirmVerdict,
  correction_note?: string,
  persistenceOverrides: Partial<ConfirmVerdictPersistence> = {},
): Promise<OutboxRecord> {
  const persistence = { ...DEFAULT_PERSISTENCE, ...persistenceOverrides };
  const field = awaitConfirmFieldOf(original);
  if (!field) {
    throw new Error(
      `pending-confirm: capture_id "${original.capture_id}" has no pending await_confirm — nothing to resolve`,
    );
  }
  // AC-1g-07 (TW-1g-PROJECT-INFER): a verdict is a fresh emission scoped to
  // the SAME project as the original — inherit its project_ref verbatim,
  // never re-select or infer. Fail-closed if the original lost it.
  const project_ref = inheritedProjectRef(original);
  // Stable per candidate/disposition IDs make a repeated tap, reload, or retry
  // resolve to the same outbox group instead of creating another action.
  const capture_id = `${original.capture_id}::dedup-${verdict}`;
  const existing = await persistence.getRecord(capture_id);
  let verdictRecord = existing ?? await persistence.saveVerdict({
    capture_id,
    client_id: `${original.client_id}::dedup-${verdict}`,
    tenant_id: original.envelope.tenant_id,
    skill: original.envelope.skill,
    skill_version: original.envelope.skill_version,
    source_refs: original.envelope.source_refs,
    parts: [],
    payload: {
      [PROJECT_REF_KEY]: project_ref,
      confirm_resolution: {
        capture_id: original.capture_id,
        confirm_token: field.confirm_token,
        verdict,
        ...(correction_note !== undefined ? { correction_note } : {}),
        // Echoed verbatim, never constructed here. Its PRESENCE is also what
        // routes this verdict to Skill #2 rather than Skill #1's own
        // photo-anchor confirm, which travels without it — so dropping it on a
        // re-emission would silently send the answer to the wrong skill.
        ...(field.resolves_capture_id !== undefined
          ? { resolves_capture_id: field.resolves_capture_id }
          : {}),
      },
    },
  });

  // A verdict must become dispatcher-visible before the ASK is hidden. If a
  // previous attempt already crossed this boundary, do not release it again:
  // HELD -> QUEUED is deliberately not a repeatable state transition.
  if (verdictRecord.state === "held") {
    verdictRecord = await persistence.releaseVerdict(verdictRecord.capture_id);
  }

  // Keep the ASK itself for audit/recovery, and add a content-free durable
  // disposition tombstone only after release succeeds. This removes the card
  // across reload immediately; the queued verdict remains the
  // server-authoritative read-back path.
  const latest = await persistence.getRecord(original.capture_id);
  if (!latest) throw new Error(`pending-confirm: original disappeared during resolution`);
  await persistence.writeOriginal({
    ...latest,
    envelope: {
      ...latest.envelope,
      payload: {
        ...latest.envelope.payload,
        [CONFIRM_DISPOSITION_KEY]: {
          confirm_token: field.confirm_token,
          verdict,
          verdict_capture_id: verdictRecord.capture_id,
        },
      },
    },
  });
  return verdictRecord;
}
